import { useState, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { CompanyAccountState } from "@/types/company/company.types";

interface FormData {
  name: string;
  nif: string;
  address: string;
  calculationManager: string;
  calculationManagerEmail: string;
  associateNumber: number | string;
  activityType: string;
  basinReference: string;
  weatherStationReference: string;
  basinName?: string;
  weatherStationName?: string;
  // Mixed activity company fields
  extractionBasinReference?: string;
  extractionWeatherStationReference?: string;
  extractionBasinName?: string;
  extractionWeatherStationName?: string;
  transformationBasinReference?: string;
  transformationWeatherStationReference?: string;
  transformationBasinName?: string;
  transformationWeatherStationName?: string;
  logoUrl?: string;
  accountState: CompanyAccountState;
}

interface ValidationField {
  isValid: boolean;
  error: string;
  touched: boolean;
}

interface ValidationState {
  name: ValidationField;
  nif: ValidationField;
  address: ValidationField;
  calculationManager: ValidationField;
  calculationManagerEmail: ValidationField;
  associateNumber: ValidationField;
  activityType: ValidationField;
  basinReference: ValidationField;
  weatherStationReference: ValidationField;
  basinName: ValidationField;
  weatherStationName: ValidationField;
  // Mixed activity company fields
  extractionBasinReference: ValidationField;
  extractionWeatherStationReference: ValidationField;
  extractionBasinName: ValidationField;
  extractionWeatherStationName: ValidationField;
  transformationBasinReference: ValidationField;
  transformationWeatherStationReference: ValidationField;
  transformationBasinName: ValidationField;
  transformationWeatherStationName: ValidationField;
  logoUrl: ValidationField;
  accountState: ValidationField;
}

export const useCompanyFormValidation = (
  formData: FormData,
  users: { id: string; email: string }[] = [],
  companies: { id: string; name: string; nif: string; associateNumber: number }[] = [],
  currentCompanyId?: string,
  originalAccountState?: CompanyAccountState,  // Add original account state for comparison
  isSubmitted?: boolean,
  usersLoading?: boolean,
) => {
  const { t } = useLanguage();

  const validateField = (name: keyof FormData, value: any, currentFormData: FormData): { isValid: boolean; error: string } => {
    // Handle undefined or null values first
    if (value === undefined || value === null || value === "") {
      switch (name) {
        case "basinName":
        case "weatherStationName":
        case "logoUrl":
        case "accountState":
        case "extractionBasinName":
        case "extractionWeatherStationName":
        case "transformationBasinName":
        case "transformationWeatherStationName":
        case "associateNumber":
          return { isValid: true, error: "" };
        // Mixed activity fields are only required for mixed activity companies
        case "extractionBasinReference":
        case "extractionWeatherStationReference":
        case "transformationBasinReference":
        case "transformationWeatherStationReference":
          if (currentFormData.activityType === "extraction_and_transformation") {
            return { isValid: false, error: t(`validation.company.${name}.required`) };
          }
          return { isValid: true, error: "" };
        default:
          return { isValid: false, error: t(`validation.company.${name}.required`) };
      }
    }

    const stringValue = typeof value === "string" ? value : String(value);

    switch (name) {
      case "name":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.name.required") };
        if (stringValue.length < 2) return { isValid: false, error: t("validation.company.name.minLength") };

        // Check for duplicate company name
        if (!isSubmitted) {
          const existingCompanyWithName = companies.find(company =>
            company.name.toLowerCase() === stringValue.trim().toLowerCase() &&
            (!currentCompanyId || company.id !== currentCompanyId)
          );

          if (existingCompanyWithName) {
            return { isValid: false, error: t("validation.company.name.alreadyExists") };
          }
        }

        return { isValid: true, error: "" };

      case "nif":
        if (!stringValue.trim()) {
          return { isValid: false, error: t("validation.company.nif.required") };
        }

        if (!/^\d{9}$/.test(stringValue)) {
          return { isValid: false, error: t("validation.company.nif.format") };
        }

        if (!isSubmitted) {
          const existingCompanyWithNif = companies.find(company =>
            company.nif === stringValue.trim() &&
            (!currentCompanyId || company.id !== currentCompanyId)
          );

          if (existingCompanyWithNif) {
            return { isValid: false, error: t("validation.company.nif.alreadyExists") };
          }
        }

        return { isValid: true, error: "" };

      case "address":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.address.required") };
        if (stringValue.length < 5) return { isValid: false, error: t("validation.company.address.minLength") };
        return { isValid: true, error: "" };

      case "calculationManager":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.manager.required") };
        if (stringValue.length < 2) return { isValid: false, error: t("validation.company.manager.minLength") };
        return { isValid: true, error: "" };

      case "calculationManagerEmail":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.managerEmail.required") };
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stringValue))
          return { isValid: false, error: t("validation.company.managerEmail.format") };
        return { isValid: true, error: "" };

      case "accountState":
        if (!value.email || value.email.trim() === "") {
          return { isValid: true, error: "" }; // Email is optional
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) {
          return { isValid: false, error: t("validation.company.email.format") };
        }

        if (currentCompanyId && originalAccountState && originalAccountState.email === value.email) {
          return { isValid: true, error: "" };
        }

        // Only check for existing user if not yet submitted and users data is fully loaded
        if (!isSubmitted && !usersLoading && users.length > 0 && formData.accountState?.email) {
          const validCompanyUsers = users.filter(user => 
            user.email && user.email.trim() !== "" && user.id
          );
          
          const usersWithEmail = validCompanyUsers.filter(user => user.email === value.email);
          
          if (usersWithEmail.length > 0) {
            if (currentCompanyId && originalAccountState && originalAccountState.email === value.email) {
              return { isValid: true, error: "" };
            }
            
            if (!currentCompanyId) {
              return { isValid: false, error: t("validation.company.email.alreadyExists") };
            }
            
            if (currentCompanyId && (!originalAccountState || !originalAccountState.email)) {
              return { isValid: false, error: t("validation.company.email.alreadyExists") };
            }
            
            return { isValid: true, error: "" };
          }
        }

        return { isValid: true, error: "" };

      case "associateNumber":
        // Allow empty values
        if (!value || value === "" || value === 0) {
          return { isValid: true, error: "" };
        }

        // Convert string to number if needed
        const numericValue = typeof value === "string" ? parseInt(value, 10) : value;

        // Check if it's a valid positive number
        if (isNaN(numericValue) || numericValue <= 0) {
          return { isValid: false, error: t("validation.company.associateNumber.invalid") };
        }

        if (!isSubmitted) {
          const existingCompanyWithAssociateNumber = companies.find(company =>
            company.associateNumber === numericValue &&
            (!currentCompanyId || company.id !== currentCompanyId)
          );

          if (existingCompanyWithAssociateNumber) {
            return { isValid: false, error: t("validation.company.associateNumber.alreadyExists") };
          }
        }

        return { isValid: true, error: "" };

      case "activityType":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.activityType.required") };
        return { isValid: true, error: "" };

      case "basinReference":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.basinReference.required") };
        return { isValid: true, error: "" };

      case "weatherStationReference":
        // If basin is empty, weather station is not required
        if (!currentFormData.basinReference || currentFormData.basinReference.trim() === "") {
          return { isValid: true, error: "" };
        }
        // If basin is selected but weather station is required
        if (!stringValue.trim()) {
          return { isValid: false, error: t("validation.company.weatherStationReference.required") };
        }
        return { isValid: true, error: "" };

      case "basinName":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.basinName.required") };
        return { isValid: true, error: "" };

      case "weatherStationName":
        if (!stringValue.trim()) return { isValid: false, error: t("validation.company.weatherStationName.required") };
        return { isValid: true, error: "" };

      // Mixed activity company validation
      case "extractionBasinReference":
        if (currentFormData.activityType === "extraction_and_transformation") {
          if (!stringValue.trim()) return { isValid: false, error: t("validation.company.extractionBasinReference.required") };
        }
        return { isValid: true, error: "" };

      case "extractionWeatherStationReference":
        if (currentFormData.activityType === "extraction_and_transformation") {
          if (!currentFormData.extractionBasinReference || currentFormData.extractionBasinReference.trim() === "") {
            return { isValid: true, error: "" };
          }
          if (!stringValue.trim()) {
            return { isValid: false, error: t("validation.company.extractionWeatherStationReference.required") };
          }
        }
        return { isValid: true, error: "" };

      case "transformationBasinReference":
        if (currentFormData.activityType === "extraction_and_transformation") {
          if (!stringValue.trim()) return { isValid: false, error: t("validation.company.transformationBasinReference.required") };
        }
        return { isValid: true, error: "" };

      case "transformationWeatherStationReference":
        if (currentFormData.activityType === "extraction_and_transformation") {
          if (!currentFormData.transformationBasinReference || currentFormData.transformationBasinReference.trim() === "") {
            return { isValid: true, error: "" };
          }
          if (!stringValue.trim()) {
            return { isValid: false, error: t("validation.company.transformationWeatherStationReference.required") };
          }
        }
        return { isValid: true, error: "" };

      case "extractionBasinName":
      case "extractionWeatherStationName":
      case "transformationBasinName":
      case "transformationWeatherStationName":
        // These are auto-populated fields, always valid
        return { isValid: true, error: "" };

      case "logoUrl":
        // logoUrl is optional, so empty value is valid
        return { isValid: true, error: "" };

      default:
        return { isValid: false, error: "" };
    }
  };

  // Calculate validation state based on current form data
  const validationState = useMemo(() => {
    const newValidation = Object.entries(formData).reduce((acc, [key, value]) => {
      const fieldName = key as keyof FormData;
      const result = validateField(fieldName, value, formData);

      acc[fieldName] = {
        ...result,
        touched: false, // Will be updated by handleInputBlur
      };

      return acc;
    }, {} as ValidationState);

    return newValidation;
  }, [formData, t, users, companies, currentCompanyId, isSubmitted, usersLoading]);

  // State to track which fields have been touched
  const [touchedFields, setTouchedFields] = useState<{ [key in keyof FormData]?: boolean }>({});

  // Combine validation state with touched state
  const validation = useMemo(() => {
    const result = { ...validationState };

    // Update touched state for each field
    Object.keys(result).forEach((key) => {
      const fieldName = key as keyof FormData;
      result[fieldName] = {
        ...result[fieldName],
        touched: touchedFields[fieldName] || false,
      };
    });

    return result;
  }, [validationState, touchedFields]);

  const handleInputBlur = (fieldName: keyof FormData) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  // Check if form is valid - exclude optional fields if they're empty
  const isFormValid = useMemo(() => {
    const validationResults = Object.entries(validation).map(([key, field]) => {
      const fieldName = key as keyof FormData;

      // Skip validation for optional fields if they're empty
      if (fieldName === "basinName" && (!formData.basinName || formData.basinName.trim() === "")) {
        return { field: fieldName, isValid: true, reason: "optional field empty" };
      }
      if (fieldName === "weatherStationName" && (!formData.weatherStationName || formData.weatherStationName.trim() === "")) {
        return { field: fieldName, isValid: true, reason: "optional field empty" };
      }
      if (fieldName === "logoUrl" && (!formData.logoUrl || formData.logoUrl.trim() === "")) {
        return { field: fieldName, isValid: true, reason: "optional field empty" };
      }
      if (fieldName === "accountState" && (!formData.accountState.email || formData.accountState.email.trim() === "")) {
        return { field: fieldName, isValid: true, reason: "optional field empty" };
      }

      // For mixed activity companies, skip validation for legacy basin/station fields
      if (formData.activityType === "extraction_and_transformation") {
        if (fieldName === "basinReference" || fieldName === "weatherStationReference" ||
            fieldName === "basinName" || fieldName === "weatherStationName") {
          return { field: fieldName, isValid: true, reason: "legacy field for mixed company" };
        }
      }

      // For single activity companies, skip validation for mixed activity fields
      if (formData.activityType !== "extraction_and_transformation") {
        if (fieldName === "extractionBasinReference" || fieldName === "extractionWeatherStationReference" ||
            fieldName === "extractionBasinName" || fieldName === "extractionWeatherStationName" ||
            fieldName === "transformationBasinReference" || fieldName === "transformationWeatherStationReference" ||
            fieldName === "transformationBasinName" || fieldName === "transformationWeatherStationName") {
          return { field: fieldName, isValid: true, reason: "mixed activity field for single company" };
        }
      }
      return { field: fieldName, isValid: field.isValid, reason: field.error || "valid" };
    });

    const invalidFields = validationResults.filter(result => !result.isValid);
    const isValid = invalidFields.length === 0;

    return isValid;
  }, [validation, formData]);

  const touchAllFields = () => {
    const allFields = Object.keys(formData).reduce((acc, key) => {
      acc[key as keyof FormData] = true;
      return acc;
    }, {} as { [key in keyof FormData]?: boolean });

    setTouchedFields(allFields);
  };

  const clearFieldValidation = (fieldName: keyof FormData) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: false,
    }));
  };

  return {
    validation,
    handleInputBlur,
    isFormValid,
    touchAllFields,
    clearFieldValidation,
  };
};
